import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DepartmentAccessLevelDTO } from '@/api/trafikportalen';
import { IconEdit, IconEye, IconSmartcard } from '@/icons';
import ToggleButtonGroup, {
  type ToggleButtonOption,
} from '../core/ToggleButtonGroup';

interface AccessLevelToggleProps {
  id: string;
  value: DepartmentAccessLevelDTO;
  onChange: (value: DepartmentAccessLevelDTO) => void;
  includeNone?: boolean;
  compact?: boolean;
  disabled?: boolean;
  loadingValue?: DepartmentAccessLevelDTO;
  labelledBy?: string;
}

export const AccessLevelToggle = ({
  id,
  value,
  onChange,
  includeNone = false,
  compact = false,
  disabled,
  loadingValue,
  labelledBy,
}: AccessLevelToggleProps) => {
  const { formatMessage } = useIntl();

  const options = useMemo(() => {
    const levels: ToggleButtonOption[] = [
      {
        value: DepartmentAccessLevelDTO.viewer,
        label: formatMessage({ defaultMessage: 'Läs' }),
        icon: <IconEye />,
      },
      {
        value: DepartmentAccessLevelDTO['bearer-manager'],
        label: formatMessage({ defaultMessage: 'Bärarhantering' }),
        icon: <IconSmartcard />,
      },
      {
        value: DepartmentAccessLevelDTO.editor,
        label: formatMessage({ defaultMessage: 'Redigering' }),
        icon: <IconEdit />,
      },
    ];

    const withNone = includeNone
      ? [
          {
            value: DepartmentAccessLevelDTO.none,
            label: formatMessage({ defaultMessage: 'Ingen' }),
          },
          ...levels,
        ]
      : levels;

    return withNone.map(({ label, ...option }) => {
      if (!option.icon) return { ...option, label };
      if (compact) return { ...option, tooltip: label };
      return option.value === value
        ? { ...option, label }
        : { ...option, label, labelClassName: 'hidden sm:block' };
    });
  }, [compact, formatMessage, includeNone, value]);

  return (
    <ToggleButtonGroup
      id={id}
      options={options}
      value={value}
      onChange={(level) => onChange(level as DepartmentAccessLevelDTO)}
      disabled={disabled}
      loadingValue={loadingValue}
      labelledBy={labelledBy}
    />
  );
};

export const AccessLevelDescription = ({
  level,
}: {
  level: DepartmentAccessLevelDTO;
}) => {
  switch (level) {
    case DepartmentAccessLevelDTO.viewer:
      return (
        <FormattedMessage defaultMessage="Kan se information men inte ändra något." />
      );
    case DepartmentAccessLevelDTO['bearer-manager']:
      return (
        <FormattedMessage defaultMessage="Läsåtkomst med bärarhantering: kan se information och byta kort och bärare på resenärer och biljetter, men inte ändra något annat." />
      );
    case DepartmentAccessLevelDTO.editor:
      return (
        <FormattedMessage defaultMessage="Kan se och ändra information." />
      );
    default:
      return null;
  }
};
